"use client"

import { useState, useTransition } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import {
  Loader2,
  LogIn,
  Mail,
  ShieldCheck,
  KeyRound,
  Trees,
  AlertCircle,
} from "lucide-react"

/**
 * Client-side login form for the custom Payload Login View.
 *
 * Posts to `/api/users/login` with `{ email, password }`. Payload sets
 * the `payload-token` cookie on the response; on success we
 * `router.push(redirect)` and `router.refresh()` so the next admin
 * page sees the new auth state.
 *
 * This component intentionally does NOT import any `@payloadcms/ui`
 * primitives — every input/button/icon is shadcn-style markup with our
 * `.adis-login-*` classes so the visual chrome reads as ADIS 95, not
 * generic Payload.
 *
 * Error handling:
 *   • 401 → "Email ou mot de passe invalide."
 *   • 423 (Locked) → "Compte verrouillé."
 *   • 429 (Rate limited) → surface server message with retry hint.
 *   • Network error → "Connexion impossible — réessayez."
 *   • Anything else → fall back to server-supplied `errors[0].message`.
 */
export function LoginViewClient({
  defaultRedirect,
}: {
  defaultRedirect: string
}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [pending, startTransition] = useTransition()

  // ?redirect=/admin/something — Payload uses this for "you tried to
  // visit X but had to log in first" flow. Fall back to /admin.
  const redirectTo = (() => {
    const r = searchParams.get("redirect")
    if (r && r.startsWith("/admin") && !r.startsWith("//")) return r
    return defaultRedirect
  })()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim() || !password) {
      setError("Veuillez saisir votre email et votre mot de passe.")
      return
    }

    startTransition(async () => {
      try {
        const res = await fetch("/api/users/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            password,
          }),
          credentials: "include",
        })

        if (res.status === 401) {
          setError("Email ou mot de passe invalide.")
          return
        }
        if (res.status === 423) {
          setError(
            "Compte verrouillé après plusieurs tentatives. Réessayez dans quelques minutes ou contactez l'administrateur.",
          )
          return
        }
        if (res.status === 429) {
          const j = await res.json().catch(() => ({}))
          setError(
            (j?.errors?.[0]?.message as string) ??
              "Trop de tentatives — patientez avant de réessayer.",
          )
          return
        }
        if (!res.ok) {
          const j = await res.json().catch(() => ({}))
          setError(
            (j?.errors?.[0]?.message as string) ??
              `Connexion refusée (HTTP ${res.status}).`,
          )
          return
        }

        // Payload set the cookie. The 2FA middleware (./middleware.ts)
        // will bounce us to /admin/2fa-challenge automatically on the
        // next admin request if the admin has 2FA enrolled.
        router.push(redirectTo)
        router.refresh()
      } catch (err) {
        setError(
          `Connexion impossible — vérifiez votre réseau et réessayez. (${(err as Error).message})`,
        )
      }
    })
  }

  return (
    <div className="adis-login-page__shell">
      {/* Brand strip */}
      <header className="adis-login-page__brand">
        <span className="adis-login-page__mark" aria-hidden>
          <Trees size={22} strokeWidth={1.7} />
        </span>
        <span className="adis-login-page__brand-text">
          <span className="adis-login-page__brand-title">ADIS 95</span>
          <span className="adis-login-page__brand-sub">
            Espace administration
          </span>
        </span>
      </header>

      <h1 id="adis-login-title" className="adis-login-page__title">
        Connectez-vous pour gérer le site.
      </h1>
      <p className="adis-login-page__lede">
        Tous les contenus, le catalogue et les commandes ADIS 95 se
        gèrent depuis cet espace. La double authentification est
        recommandée — vous pourrez l&apos;activer après la première
        connexion.
      </p>

      <form
        className="adis-login-page__form"
        onSubmit={onSubmit}
        noValidate
        aria-describedby={error ? "adis-login-err" : undefined}
      >
        {error ? (
          <div
            id="adis-login-err"
            role="alert"
            className="adis-login-page__error"
          >
            <AlertCircle size={16} aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}

        <label className="adis-login-page__field">
          <span className="adis-login-page__field-label">
            <Mail size={14} aria-hidden />
            Adresse email
          </span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@adis95.fr"
            className="adis-login-page__input"
            aria-required="true"
            disabled={pending}
          />
        </label>

        <label className="adis-login-page__field">
          <span className="adis-login-page__field-label">
            <KeyRound size={14} aria-hidden />
            Mot de passe
          </span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="adis-login-page__input"
            aria-required="true"
            disabled={pending}
          />
        </label>

        <div className="adis-login-page__actions">
          <Link
            href="/admin/forgot"
            className="adis-login-page__forgot"
          >
            Mot de passe oublié ?
          </Link>
          <button
            type="submit"
            className="adis-login-page__submit"
            disabled={pending}
          >
            {pending ? (
              <>
                <Loader2
                  size={14}
                  aria-hidden
                  className="adis-login-page__spin"
                />
                Connexion en cours
              </>
            ) : (
              <>
                <LogIn size={14} aria-hidden />
                Se connecter
              </>
            )}
          </button>
        </div>
      </form>

      <footer className="adis-login-page__security">
        <ShieldCheck size={16} aria-hidden className="adis-login-page__security-icon" />
        <span>
          Activez la <strong>double authentification</strong> dans «&nbsp;Mon
          compte&nbsp;→&nbsp;Sécurité&nbsp;» dès la première connexion.
        </span>
      </footer>
    </div>
  )
}
